import { NavigateFunction } from 'react-router-dom'
import { MAX_PAGE_SIZE, privacyTypes, urlList } from '../constants/constants'
import i18n from '../i18n/i18n'
import {
  DataSource,
  DataSourceConfigType,
  EgnyteSecrets,
  SourceType,
} from '../store/reducers/dataSourceReducer'
import { PrivacyType, TranslationDictionary } from '../types/generalTypes'
import apiService from './apiService'

const baseUrl = urlList.DATA_SOURCES

interface GetDataSourceListProps {
  page?: number
  pageSize?: number
}

const getDataSourceList = async ({
  page,
  pageSize,
}: GetDataSourceListProps) => {
  const url =
    page && pageSize
      ? `${baseUrl}?page=${page}&pageSize=${pageSize}`
      : `${baseUrl}?page=1&pageSize=${MAX_PAGE_SIZE}`
  return apiService
    .fetchItems(url)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

const getDataSourceTitle = (source?: DataSource) => {
  if (!source) {
    return ''
  }
  const title = source.title
  if (typeof title === 'string') {
    return title
  } else {
    const languages = Object.keys(title)
    if (languages.length > 0) {
      return title[i18n.language]
    } else {
      return ''
    }
  }
}

export interface DataSourceTypeItem {
  id: string
  name: string
}

const mapSourceToTypeItem = (
  source: DataSource,
  isSearchable: boolean
): DataSourceTypeItem => {
  const { organizationName, typeCode, typeName, isInternal } = source

  const id = isSearchable ? typeCode : isInternal ? organizationName : typeCode

  const name = isSearchable
    ? typeName
    : isInternal
    ? organizationName
    : typeName

  return { id, name }
}
const getTypeList = (
  sources: DataSource[],
  isSearchable: boolean = false
): DataSourceTypeItem[] => {
  const typeArray = sources.map((source) =>
    mapSourceToTypeItem(source, isSearchable)
  )

  const uniqueTypeList = typeArray.filter(
    (type, pos, self) =>
      !!type.id && self.findIndex((item) => item.id === type.id) === pos
  )

  uniqueTypeList.sort((a, b) => a.name.localeCompare(b.name))

  return uniqueTypeList
}

interface GetDataSourceDetailsProps {
  dataSourceId: string
}

const getDataSourceDetails = async ({
  dataSourceId,
}: GetDataSourceDetailsProps) => {
  return apiService
    .fetchItems(`${baseUrl}/${dataSourceId}`)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

interface UpdateDataSourceDetailsProps {
  title: string | TranslationDictionary
  description: string
  visibility: PrivacyType
  participants: string[]
  contactName: string
  contactEmail: string
  dataSourceId: string
  navigate: NavigateFunction
}

const updateDataSourceDetails = async ({
  title,
  description,
  visibility,
  participants,
  contactName,
  contactEmail,
  dataSourceId,
  navigate,
}: UpdateDataSourceDetailsProps) => {
  const params = {
    title,
    description,
    visibility,
    participants: visibility === privacyTypes.PRIVATE ? participants : [],
    contactEmail,
    contactName,
  }

  return apiService
    .updateItem(`${baseUrl}/${dataSourceId}`, navigate, params)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios update error', error)
      throw error
    })
}

interface DeleteDataSourceProps {
  dataSourceId: string
  navigate: NavigateFunction
}

const deleteDataSource = async ({
  dataSourceId,
  navigate,
}: DeleteDataSourceProps) => {
  return apiService
    .deleteItem(`${baseUrl}/${dataSourceId}`, navigate)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios delete error', error)
      throw error
    })
}

interface CreateDataSourceProps {
  title: string | TranslationDictionary
  description: string
  sourceType: SourceType
  visibility: PrivacyType
  participants: string[]
  navigate: NavigateFunction
  config: DataSourceConfigType
  secrets: EgnyteSecrets
  contactEmail: string
  contactName: string
}

const createDataSource = async ({
  title,
  description,
  visibility,
  sourceType,
  participants,
  config,
  navigate,
  secrets,
  contactName,
  contactEmail,
}: CreateDataSourceProps) => {
  const params = {
    title,
    description,
    visibility,
    config,
    sourceType,
    participants: visibility === privacyTypes.PRIVATE ? participants : [],
    navigate,
    secrets,
    contactEmail,
    contactName,
  }

  return apiService
    .saveItem(`${baseUrl}`, navigate, params)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios create error', error)
      throw error
    })
}

const getDataSourceType = async () => {
  return apiService
    .fetchItems(urlList.DATA_SOURCE_TYPES)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}
const getDataSourceFiles = async ({
  dataSourceId,
  format,
}: {
  dataSourceId: string
  format?: string
}) => {
  const url = format
    ? `${baseUrl}/${dataSourceId}/files?format=${format}`
    : `${baseUrl}/${dataSourceId}/files`

  return apiService
    .fetchWithRetry(url)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

const exportDataSourceList = async () => {
  return apiService
    .fetchItems(`${baseUrl}/csv`)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

interface ExportDataSourceDetailsProp {
  dataSourceId: string
}

const exportDataSourceDetails = async ({
  dataSourceId,
}: ExportDataSourceDetailsProp) => {
  return apiService
    .fetchItems(`${baseUrl}/${dataSourceId}/csv`)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

interface GetAllIterationsProps {
  dataSourceId: string
  documentId: string
}

const getAllIterations = async ({
  dataSourceId,
  documentId,
}: GetAllIterationsProps) => {
  return apiService.fetchItems(
    `${baseUrl}/${dataSourceId}/files/iterations/documentId=${documentId}`
  )
}

interface GetLatestIterationProps {
  dataSourceId: string
  documentId: string
}

const getLatestIteration = async ({
  dataSourceId,
  documentId,
}: GetLatestIterationProps) => {
  return apiService.fetchItems(
    `${baseUrl}/${dataSourceId}/files/iterations/documentId=${documentId}/latest`
  )
}

const getPermittedUsersOfDataSource = async ({
  dataSourceId,
}: {
  dataSourceId: string
}) => {
  return apiService.fetchItems(`${baseUrl}/${dataSourceId}/permittedUsers`)
}

const dataSourceService = {
  getDataSourceList,
  createDataSource,
  deleteDataSource,
  exportDataSourceDetails,
  exportDataSourceList,
  getAllIterations,
  getDataSourceDetails,
  getDataSourceTitle,
  getDataSourceType,
  getTypeList,
  updateDataSourceDetails,
  getDataSourceFiles,
  getLatestIteration,
  getPermittedUsersOfDataSource,
}
export default dataSourceService
